import axios from 'axios';
import { createWithEqualityFn } from 'zustand/traditional';
import { getHmrStoreState, initHmrStore } from '@client/helpers/vite.helpers';
import {
  CreateProjectLabel,
  ProjectLabel,
  ProjectLabels,
} from '@shared/models/project-label.model';
import { omit } from '@shared/helpers/object.helpers';

type OrganizationLabels = ProjectLabels & { loaded: boolean };
const EMPTY: OrganizationLabels = {
  labels: [],
  assignments: {},
  loaded: false,
};
type LabelsState = { organizations: Record<string, OrganizationLabels> };
const STORE_NAME = 'project-labels';
export const useLabelsStore = createWithEqualityFn<LabelsState>(() => ({
  organizations: {},
  ...getHmrStoreState<LabelsState>(STORE_NAME),
}));
initHmrStore(STORE_NAME, useLabelsStore);
const configFor = (organization: string) => ({
  headers: { Organization: organization },
});

export const loadProjectLabels = async (organization: string) => {
  const { data } = await axios.get<ProjectLabels>(
    'projects/labels',
    configFor(organization),
  );
  useLabelsStore.setState((state) => ({
    organizations: {
      ...state.organizations,
      [organization]: { ...data, loaded: true },
    },
  }));
};

export const createProjectLabel = async (
  organization: string,
  payload: CreateProjectLabel,
) => {
  const { data: label } = await axios.post<ProjectLabel>(
    'projects/labels',
    payload,
    configFor(organization),
  );
  useLabelsStore.setState((state) => {
    const current = state.organizations[organization] ?? EMPTY;
    return {
      organizations: {
        ...state.organizations,
        [organization]: {
          ...current,
          labels: [
            ...current.labels.filter((item) => item.id !== label.id),
            label,
          ].sort((a, b) => a.name.localeCompare(b.name)),
        },
      },
    };
  });
  return label;
};

export const assignProjectLabels = async (
  organization: string,
  projectId: string,
  labelIds: string[],
) => {
  const { data } = await axios.put<string[]>(
    `projects/${projectId}/labels`,
    { labelIds },
    configFor(organization),
  );
  useLabelsStore.setState((state) => {
    const current = state.organizations[organization] ?? EMPTY;
    return {
      organizations: {
        ...state.organizations,
        [organization]: {
          ...current,
          assignments: { ...current.assignments, [projectId]: data },
        },
      },
    };
  });
};

/** Remove deleted projects from every cached organization without changing other assignments. */
export const removeProjectLabelAssignments = (projectId: string) => {
  useLabelsStore.setState((state) => ({
    organizations: Object.fromEntries(
      Object.entries(state.organizations).map(([organization, data]) => [
        organization,
        { ...data, assignments: omit(data.assignments, projectId) },
      ]),
    ),
  }));
};
